import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store'
import {
  PlatformTourPhase,
  closePlatformTour,
  setPlatformTourPhase,
  showPlatformTourStop,
  showPlatformTourWelcome
} from '../../../../store/slices/uiSlice'
import { useUpdateUser } from '../../../../apiLayer'
import { useUser } from '../../../../hooks/useUser'
import { PlatformTourOutcome } from '../../../../../../pure-js/enums/PlatformTourOutcome'

/** Writes how they left the tour, so the onboarding page stops offering it. */
const useRecordPlatformTourOutcome = () => {
  const { data: user } = useUser()
  const { mutate } = useUpdateUser()

  return useCallback(
    (outcome: PlatformTourOutcome) => {
      if (!user?.id) return

      mutate({ id: user.id, userData: { tasks: { platformTour: { outcome, at: new Date().toISOString() } } } })
    },
    [user?.id, mutate]
  )
}

/** The tour's state in this visit and every move it can make. */
export const usePlatformTourControls = () => {
  const dispatch = useAppDispatch()
  const tour = useAppSelector((state) => state.ui.platformTour)
  const recordOutcome = useRecordPlatformTourOutcome()

  return useMemo(
    () => ({
      tour,
      showWelcome: () => dispatch(showPlatformTourWelcome()),
      showStop: (stopIndex: number) => dispatch(showPlatformTourStop(stopIndex)),
      resume: () => dispatch(showPlatformTourStop(tour?.stopIndex ?? 0)),
      pause: () => dispatch(setPlatformTourPhase(PlatformTourPhase.PAUSED)),
      finish: () => {
        recordOutcome(PlatformTourOutcome.FINISHED)
        dispatch(setPlatformTourPhase(PlatformTourPhase.DONE))
      },
      skip: () => {
        recordOutcome(PlatformTourOutcome.SKIPPED)
        dispatch(setPlatformTourPhase(PlatformTourPhase.ENDED))
      },
      end: () => dispatch(setPlatformTourPhase(PlatformTourPhase.ENDED)),
      close: () => dispatch(closePlatformTour())
    }),
    [tour, dispatch, recordOutcome]
  )
}
